import { Component, DestroyRef, OnInit, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { filter } from 'rxjs';
import { CurrentContextService } from '../../core/context/current-context.service';
import { LiveUpdateEventTypes, growthTwinTopic, liveStatusTranslationKey } from '../../core/live-updates/live-update.models';
import { LiveUpdateService } from '../../core/live-updates/live-update.service';
import { LocalizationService } from '../../core/localization/localization.service';
import { InfoHintComponent } from '../../shared/design-system/info-hint.component';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { GrowthScenario, GrowthScenarioCompareResult, GrowthTwinService } from './growth-twin.service';

@Component({
  standalone: true,
  selector: 'ain-growth-twin-scenarios',
  imports: [AinPageComponent, InfoHintComponent, TableModule, FormsModule, ButtonModule, InputTextModule, SelectModule],
  template: `
    <ain-page [title]="t('growth.twin.scenarios.title')" [description]="t('growth.twin.scenarios.description')">
      <ain-info-hint
        page-title-addon
        [titleKey]="'growth.twin.scenarios.help.title'"
        [descriptionKey]="'growth.twin.scenarios.help.description'"
        [bulletKeys]="['growth.twin.scenarios.help.bullets.whatIf', 'growth.twin.scenarios.help.bullets.compare', 'growth.twin.scenarios.help.bullets.live']" />

      <section class="card list-card">
        <div class="toolbar">
          <input
            pInputText
            type="text"
            [(ngModel)]="searchName"
            [placeholder]="t('growth.twin.scenarios.searchName')" />
          <p-select
            [options]="statusOptions"
            [(ngModel)]="statusFilter"
            optionLabel="label"
            optionValue="value"
            [showClear]="true"
            appendTo="body"
            [placeholder]="t('growth.twin.scenarios.filterStatus')" />
          <button pButton type="button" [label]="t('growth.twin.scenarios.search')" (click)="load()"></button>
          <button pButton type="button" [label]="t('growth.twin.scenarios.refresh')" severity="secondary" (click)="load()"></button>
          <span class="live-badge">{{ t(liveStatusKey(liveUpdateService.connectionState())) }}</span>
        </div>
        <div class="toolbar">
          <button pButton type="button" [label]="t('growth.twin.scenarios.addNew')" (click)="openCreate()"></button>
          <button
            pButton
            type="button"
            [label]="t('growth.twin.scenarios.editSelected')"
            severity="secondary"
            [disabled]="!selected()"
            (click)="openEditSelected()"></button>
          <button
            pButton
            type="button"
            [label]="t('growth.twin.scenarios.actions.compare')"
            severity="secondary"
            [disabled]="compareSelection().length < 2"
            [loading]="comparing()"
            (click)="compareSelected()"></button>
        </div>
        <p-table
          [value]="filteredRows()"
          selectionMode="single"
          [selection]="selected()"
          (selectionChange)="onSelect($event)"
          dataKey="id">
          <ng-template pTemplate="header">
            <tr>
              <th style="width:3rem"></th>
              <th>{{ t('growth.twin.scenarios.columns.name') }}</th>
              <th>{{ t('growth.twin.scenarios.columns.status') }}</th>
              <th>{{ t('growth.twin.scenarios.columns.boundary') }}</th>
              <th>{{ t('growth.twin.scenarios.columns.simulationRef') }}</th>
            </tr>
          </ng-template>
          <ng-template pTemplate="body" let-row>
            <tr [pSelectableRow]="row">
              <td>
                <input
                  type="checkbox"
                  [checked]="isCompareSelected(row.id)"
                  (click)="$event.stopPropagation(); toggleCompare(row)" />
              </td>
              <td>
                <button type="button" class="link-btn" (click)="openDetail(row.id)">{{ row.name }}</button>
              </td>
              <td>{{ row.status }}</td>
              <td>{{ row.boundary }}</td>
              <td>{{ row.simulationRef }}</td>
            </tr>
          </ng-template>
        </p-table>
        @if (errorMessage()) {
          <p class="message error">{{ errorMessage() }}</p>
        }
      </section>

      @if (compareResult()) {
        <section class="card list-card">
          <h3>{{ t('growth.twin.scenarios.compare.title') }}</h3>
          @if (compareResult()!.recommendationSummary) {
            <p class="muted">{{ compareResult()!.recommendationSummary }}</p>
          }
          <p-table [value]="compareResult()!.rows">
            <ng-template pTemplate="header">
              <tr>
                <th>{{ t('growth.twin.scenarios.columns.name') }}</th>
                <th>{{ t('growth.twin.scenarios.columns.status') }}</th>
                <th>{{ t('growth.twin.scenarios.compare.columns.leadDelta') }}</th>
                <th>{{ t('growth.twin.scenarios.compare.columns.revenueDelta') }}</th>
                <th>{{ t('growth.twin.scenarios.compare.columns.confidence') }}</th>
              </tr>
            </ng-template>
            <ng-template pTemplate="body" let-row>
              <tr [class.recommended]="row.scenarioId === compareResult()!.recommendedScenarioId">
                <td>
                  <button type="button" class="link-btn" (click)="openDetail(row.scenarioId)">{{ row.name }}</button>
                </td>
                <td>{{ row.status }}</td>
                <td>{{ row.leadDelta ?? '-' }}</td>
                <td>{{ row.revenueDelta ?? '-' }}</td>
                <td>{{ row.confidenceLow }} – {{ row.confidenceHigh }}</td>
              </tr>
            </ng-template>
          </p-table>
        </section>
      }
    </ain-page>
  `,
  styles: [`
    .card { border: 1px solid #e5e7eb; border-radius: 10px; padding: 12px; background: #fff; }
    .list-card { display: grid; gap: 10px; margin-bottom: 12px; }
    .toolbar { display: flex; gap: 8px; flex-wrap: wrap; align-items: center; }
    .link-btn { border: 0; background: none; color: #1d4ed8; cursor: pointer; font-weight: 600; padding: 0; }
    .link-btn:hover { text-decoration: underline; }
    .message { margin: 0; font-size: 13px; }
    .message.error { color: #b91c1c; }
    .muted { color: #64748b; margin: 0; }
    .recommended { background: #ecfdf5; }
    .live-badge { font-size: 0.85rem; color: #64748b; }
    h3 { margin: 0; }
  `]
})
export class GrowthTwinScenariosComponent implements OnInit {
  private readonly api = inject(GrowthTwinService);
  private readonly localization = inject(LocalizationService);
  private readonly router = inject(Router);
  private readonly context = inject(CurrentContextService);
  private readonly destroyRef = inject(DestroyRef);
  readonly liveUpdateService = inject(LiveUpdateService);
  readonly liveStatusKey = liveStatusTranslationKey;

  readonly rows = signal<GrowthScenario[]>([]);
  readonly filteredRows = signal<GrowthScenario[]>([]);
  readonly selected = signal<GrowthScenario | null>(null);
  readonly compareSelection = signal<GrowthScenario[]>([]);
  readonly compareResult = signal<GrowthScenarioCompareResult | null>(null);
  readonly comparing = signal(false);
  readonly errorMessage = signal('');

  searchName = '';
  statusFilter: string | null = null;

  readonly statusOptions = [
    { label: 'Draft', value: 'Draft' },
    { label: 'Simulated', value: 'Simulated' },
    { label: 'Failed', value: 'Failed' },
    { label: 'Archived', value: 'Archived' }
  ];

  private subscribedTopic: string | null = null;

  ngOnInit(): void {
    this.liveUpdateService.events$
      .pipe(filter(e => e.eventType === LiveUpdateEventTypes.growthTwinChanged), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.load());
    this.destroyRef.onDestroy(() => {
      if (this.subscribedTopic) {
        void this.liveUpdateService.unsubscribe(this.subscribedTopic);
        this.subscribedTopic = null;
      }
    });
    void this.setupLiveSubscription();
    this.load();
  }

  load(): void {
    this.errorMessage.set('');
    this.api.listScenarios(this.statusFilter ?? undefined).subscribe({
      next: data => {
        this.rows.set(data);
        this.applySearch();
      },
      error: () => {
        this.rows.set([]);
        this.filteredRows.set([]);
        this.selected.set(null);
        this.errorMessage.set(this.t('growth.twin.scenarios.messages.loadFailed'));
      }
    });
  }

  applySearch(): void {
    const term = this.searchName.trim().toLowerCase();
    const filtered = !term
      ? this.rows()
      : this.rows().filter(item => item.name.toLowerCase().includes(term));
    this.filteredRows.set(filtered);
    this.selected.set(filtered[0] ?? null);
  }

  onSelect(row: GrowthScenario | null): void {
    this.selected.set(row);
  }

  openCreate(): void {
    void this.router.navigate(['/growth/twin/scenarios/new']);
  }

  openEditSelected(): void {
    const row = this.selected();
    if (!row) {
      return;
    }
    this.openDetail(row.id);
  }

  openDetail(id: string): void {
    void this.router.navigate(['/growth/twin/scenarios', id]);
  }

  isCompareSelected(id: string): boolean {
    return this.compareSelection().some(item => item.id === id);
  }

  toggleCompare(row: GrowthScenario): void {
    const current = this.compareSelection();
    if (current.some(item => item.id === row.id)) {
      this.compareSelection.set(current.filter(item => item.id !== row.id));
      return;
    }
    this.compareSelection.set([...current, row]);
  }

  compareSelected(): void {
    const ids = this.compareSelection().map(item => item.id);
    if (ids.length < 2) {
      return;
    }
    this.comparing.set(true);
    this.errorMessage.set('');
    this.api.compareScenarios(ids).subscribe({
      next: result => {
        this.compareResult.set(result);
        this.comparing.set(false);
      },
      error: () => {
        this.comparing.set(false);
        this.errorMessage.set(this.t('growth.twin.scenarios.messages.compareFailed'));
      }
    });
  }

  t(key: string): string {
    return this.localization.translate(key);
  }

  private async setupLiveSubscription(): Promise<void> {
    const tenantId = this.context.context()?.tenantId;
    if (!tenantId) {
      return;
    }
    this.subscribedTopic = growthTwinTopic(tenantId);
    await this.liveUpdateService.subscribe(this.subscribedTopic);
  }
}
